"use client";

import { useMemo } from "react";

import { DOCS_URL, FEEDBACK_URL, PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from "@/constants/urls";
import { useStatsigConfig } from "@/contexts/statsig-config-context";
import { useAgentModeEnabled } from "@/hooks/use-agent-mode-enabled";

type NavGate = "agentMode" | "cardVault";

interface NavItem {
  tx: string;
  path: string;
  subAccountRequired?: boolean;
  newTab?: boolean;
  badge?: "new";
  gate?: NavGate;
  desktopOnly?: true;
}

interface NavItemGroup {
  tx: string;
  options: NavItem[];
  subAccountRequired?: boolean;
  gate?: NavGate;
}

type NavItems = Array<NavItem | NavItemGroup>;

const NAV_ITEMS: NavItems = [
  { tx: "trade", path: "/trade" },
  { tx: "agent", path: "/agent", badge: "new", gate: "agentMode", desktopOnly: true },
  { tx: "markets", path: "/markets" },
  { tx: "leaderboard", path: "/leaderboard" },
  { tx: "portfolio", path: "/portfolio" },
  { tx: "vaults", path: "/vaults" },
  { tx: "points", path: "/points" },
  { tx: "rewards", path: "/rewards" },
  // Visible to everyone: affiliates land on their portal, everyone else on the
  // apply gate — the entry point IS the acquisition surface.
  { tx: "affiliates", path: "/affiliates" },
  {
    tx: "more",
    options: [
      { tx: "accounts", path: "/accounts" },
      { tx: "api", path: "/api" },
      { tx: "announcements", path: "/announcements" },
      { tx: "cardVault", path: "/card-vault", gate: "cardVault" },
      { tx: "docs", path: DOCS_URL, newTab: true },
      { tx: "feedback", path: FEEDBACK_URL, newTab: true },
      { tx: "termsOfService", path: TERMS_OF_SERVICE_URL, newTab: true },
      { tx: "privacyPolicy", path: PRIVACY_POLICY_URL, newTab: true },
    ],
  },
];

export function useNavItems() {
  const cardVault = useStatsigConfig().cardVault.enabled;
  const agentMode = useAgentModeEnabled();
  return useMemo(() => {
    const gates: Record<NavGate, boolean> = { agentMode, cardVault };
    const isVisible = (item: NavItem | NavItemGroup) => !item.gate || gates[item.gate];
    return NAV_ITEMS.filter(isVisible).map((item) =>
      "options" in item ? { ...item, options: item.options.filter(isVisible) } : item,
    );
  }, [agentMode, cardVault]);
}

export function flattenNavItems(items: NavItems) {
  return items.flatMap((item) => ("options" in item ? item.options : [item]));
}

export const getIsPathActive = (pathname: string, path: string) => pathname.startsWith(path);
